import {
  type ColumnFiltersState,
  type ColumnPinningState,
  type ColumnVisibilityState,
  type ExpandedState,
  type RowPinningState,
  type SortingState,
  useTable,
} from "@tanstack/react-table";
import { useEffect, useEffectEvent, useMemo, useState } from "react";

import type { DataModel } from "../../../../models/data-model";
import type { DataTableColumnDef } from "./table-features";
import type { Column, DataTableProps } from "./types";

import { initialColumnVisibility } from "./column-visibility";
import { dataTableFeatures } from "./table-features";
import { getColumnId } from "./types";
import {
  useTablePersistenceInitial,
  useTablePersistenceRouteName,
  useTablePersistenceWriteBack,
} from "./use-table-persistence";

export interface UseDataTableArgs<T extends object> {
  model: DataModel<T>;
  filteredColumns: Column<T>[];
  builtColumns: DataTableColumnDef<T>[];
  itemKeyField: keyof T;
  rowsPerPage: number;
  persistState: boolean;
  persistStateKey: string | undefined;
  canSelect: "single" | "multi" | undefined;
  /**
   * Stringified row ids to pin to the top — DataTable derives this from
   * `model.selected` and passes it through so pinning re-runs whenever the
   * model's selection changes (the model is the source of truth for
   * selection; TanStack's RowSelection is unused).
   */
  pinnedTopIds: string[];
  pinnedStart: string[];
  pinnedEnd: string[];
  initialColumnFilters?: ColumnFiltersState;
  filterDeps?: unknown[];
  getSubRows?: DataTableProps<T>["getSubRows"];
  serverPagination?: DataTableProps<T>["serverPagination"];
}

// Stable reference for the empty-data fallback so TanStack doesn't think
// the data prop changed on every render when `model.data` is null.
const EMPTY_DATA: unknown[] = [];

export function useDataTable<T extends object>(args: UseDataTableArgs<T>) {
  const {
    model,
    filteredColumns,
    builtColumns,
    itemKeyField,
    rowsPerPage,
    persistState,
    persistStateKey,
    canSelect,
    pinnedTopIds,
    pinnedStart,
    pinnedEnd,
    initialColumnFilters,
    filterDeps,
    getSubRows,
    serverPagination,
  } = args;

  const data = model.data ?? (EMPTY_DATA as T[]);

  const routeName = useTablePersistenceRouteName(persistState, persistStateKey);
  const initialState = useTablePersistenceInitial({
    active: persistState,
    defaultPageSize: rowsPerPage,
    routeName,
  });

  // Permission-based column visibility isn't applied here — callers filter
  // disallowed columns out of `filteredColumns` before they reach this hook,
  // so the table only ever sees columns the user is allowed to see.
  const [sorting, setSorting] = useState<SortingState>(() => {
    // A restored session wins even when empty — the user explicitly cleared
    // sorting. Only fall back to column initSort when nothing was saved.
    if (initialState.sorting != null) return initialState.sorting;
    const initial: SortingState = [];
    for (let i = 0; i < filteredColumns.length; i++) {
      const col = filteredColumns[i];
      if (col.sort && col.initSort) {
        initial.push({
          id: getColumnId(col, i),
          desc: col.initSort === "desc",
        });
      }
    }
    return initial;
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    initialState.columnFilters ?? initialColumnFilters ?? [],
  );
  const [pagination, setPagination] = useState({
    pageIndex: initialState.pagination?.pageIndex ?? 0,
    pageSize: initialState.pagination?.pageSize ?? rowsPerPage,
  });
  const [expanded, setExpanded] = useState<ExpandedState>({});

  // An effect event, so a caller passing a fresh callback each render doesn't
  // refetch the same page.
  const onServerPageChange = useEffectEvent(
    (page: { pageIndex: number; pageSize: number }) =>
      serverPagination?.onChange(page),
  );
  useEffect(() => {
    onServerPageChange(pagination);
  }, [pagination]);
  // TanStack's filtered row model is memoized on the rows and the filter
  // state, so a predicate reading external data (e.g. a store that loads
  // after the rows) would keep its stale result — a restored filter could
  // hide every row forever. Bumping the filter state's identity when a
  // declared dep changes forces active filters to re-evaluate.
  useEffect(
    () => {
      setColumnFilters((filters) =>
        filters.length > 0 ? [...filters] : filters,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller-declared external filter inputs, React-deps style
    filterDeps ?? [],
  );
  // Filter-only columns (`hidden`) stay registered with TanStack so their
  // filterFn applies, but never render — visibility only affects rendering.
  // On top of that sit the user's own hides from the column-visibility panel,
  // restored from the persisted session when there is one.
  const [columnVisibility, setColumnVisibility] =
    useState<ColumnVisibilityState>(() =>
      initialColumnVisibility(filteredColumns, initialState.columnVisibility),
    );

  // Selection lives in the MobX model — mirror it into TanStack's rowPinning
  // so pinned-to-top rendering works. Memo key is the joined ids so the
  // object identity is stable across renders where the selection didn't
  // change (TanStack treats `state` as controlled).
  const pinnedTopIdsKey = pinnedTopIds.join("|");
  const rowPinning = useMemo<RowPinningState>(
    () => ({ top: pinnedTopIds, bottom: [] }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- key string captures the array contents
    [pinnedTopIdsKey],
  );

  const pinnedStartKey = pinnedStart.join("|");
  const pinnedEndKey = pinnedEnd.join("|");
  // v9 pins by logical direction (`start`/`end`) rather than `left`/`right`.
  const columnPinning = useMemo<ColumnPinningState>(
    () => ({ start: pinnedStart, end: pinnedEnd }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- key strings capture array contents
    [pinnedStartKey, pinnedEndKey],
  );

  const table = useTable<typeof dataTableFeatures, T>({
    // Features, row models and the filter/sort fn registries all live in this
    // one shared set now — see `table-features.ts`.
    features: dataTableFeatures,
    data,
    columns: builtColumns,
    state: {
      sorting,
      columnFilters,
      pagination,
      columnVisibility,
      rowPinning,
      columnPinning,
      expanded,
    },
    enableColumnFilters: true,
    enableRowPinning: !!canSelect,
    keepPinnedRows: true,
    enableSortingRemoval: true,
    getRowId: (row, _index, parent) =>
      parent
        ? `${parent.id}>${String(row[itemKeyField])}`
        : String(row[itemKeyField]),
    getSubRows,
    // Sub-rows follow their parent onto its page instead of taking slots of
    // their own, and a refetch (e.g. after an edit) keeps what's open.
    paginateExpandedRows: false,
    autoResetExpanded: false,
    onExpandedChange: setExpanded,
    manualPagination: !!serverPagination,
    rowCount: serverPagination?.rowCount,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    onColumnVisibilityChange: setColumnVisibility,
  });

  useTablePersistenceWriteBack({
    table,
    active: persistState,
    defaultPageSize: rowsPerPage,
    routeName,
  });

  return table;
}
